import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { isEmpty, isNil, propEq, find } from 'ramda'
import { FormattedMessage, useIntl } from 'react-intl'

import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY, CANCEL } from 'app/constants/button'

import {
  openWarningDialog,
  closeWarningDialog,
} from 'app/store/actions/warningDialog.actions'
import { useDispatch } from 'react-redux'
import { twMerge } from 'tailwind-merge'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import CountrySelector from 'app/componentV4/selectors/CountrySelector'
import RegionSelector from 'app/componentV4/selectors/RegionSelector'
import clsx from 'clsx'
import effects from 'app/effects/effects'
import { isEmptyOrNil } from 'libs/empty'

const useStyles = makeStyles((theme) => ({
  formRoot: {
    '& .MuiFormControl-root': {
      '& .label-error': {
        marginTop: '4px',
      },
    },
  },
  inputFormat: {
    '& .MuiInputBase-root': {
      color: 'rgb(24 53 96 / var(--tw-text-opacity)) !important',
    },
    '& fieldset': {
      height: '53px !important',
      borderRadius: '.8rem !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 17px) scale(1)',
    },
  },
  labelInput: {
    '& span': {
      color: '#EF4444',
    },
  },
}))

const getRegionTypes = (regionType, formatMessage) => {
  switch (regionType) {
    case 'province':
      return formatMessage({ id: 'crm.form.stateType.province' })
    case 'state':
      return formatMessage({ id: 'crm.form.stateType.state' })
    case 'island':
      return formatMessage({ id: 'crm.form.stateType.island' })
    case 'area':
      return formatMessage({ id: 'crm.form.stateType.area' })
    case 'parish':
      return formatMessage({ id: 'crm.form.stateType.parish' })
    case 'perfecture':
      return formatMessage({ id: 'crm.form.stateType.perfecture' })
    case 'district':
      return formatMessage({ id: 'crm.form.stateType.district' })
    case 'department':
      return formatMessage({ id: 'crm.form.stateType.department' })
    case 'oblast':
      return formatMessage({ id: 'crm.form.stateType.oblast' })
    case 'emirate':
      return formatMessage({ id: 'crm.form.stateType.emirate' })

    default:
      return ''
  }
}

const AddressInformationForm = ({
  subscriber,
  countries,
  onAddressChange,
  onAddressReset,
}) => {
  const formik = useFormikContext()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isButtonDisabled = !formik.dirty
  const isButtonLoading = formik.isValidating || formik.isSubmitting
  const dispatch = useDispatch()
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const [regionTypeOfPhysical, setRegionTypeOfPhysical] = useState(null)
  const [regionTypeOfMilling, setRegionTypeOfMilling] = useState(null)
  const [regionPhysicalOptions, setRegionPhysicalOptions] = useState([])
  const [regionMailingOptions, setRegionMailingOptions] = useState([])

  const onCancelClick = () => {
    if (formik.dirty) {
      const payload = {
        title: formatMessage({ id: 'dialog.discard.title' }),
        description: formatMessage({ id: 'dialog.discard.body' }),
        submitText: formatMessage({
          id: 'draft.notification.create.popup.title',
        }),
        closeText: formatMessage({ id: 'dialog.discard.close' }),
        type: CANCEL,
        onSubmit: () => {
          formik.resetForm()
          onAddressReset()
          dispatch(closeWarningDialog())
        },
        onClose: () => {
          dispatch(closeWarningDialog())
        },
      }
      dispatch(openWarningDialog(payload))
    }
  }

  useEffect(() => {
    if (formik.dirty) {
      onAddressChange(formik.dirty)
    } else {
      onAddressReset()
    }
  }, [formik.dirty])

  useEffect(() => {
    if (formik.values.physicalCountryCode) {
      const regionPhysical = find(
        (item) => item.id === formik.values.physicalCountryCode,
        countries,
      )
      setRegionTypeOfPhysical(regionPhysical.regionType)
    }
    if (formik.values.mailingCountryCode) {
      const regionMailing = find(
        (item) => item.id === formik.values.mailingCountryCode,
        countries,
      )
      setRegionTypeOfMilling(regionMailing.regionType)
    }
  }, [formik.dirty])

  useEffect(() => {
    if (!isEmptyOrNil(formik.values.physicalCountryCode)) {
      effects.apis
        .getStatesByCountryId({ countryId: formik.values.physicalCountryCode })
        .then((states) => {
          if (isEmpty(states)) {
            setRegionPhysicalOptions([])
          } else {
            setRegionPhysicalOptions(states.data)
          }
        })
    } else {
      setRegionPhysicalOptions([])
    }
  }, [formik.values.physicalCountryCode])

  useEffect(() => {
    if (!isEmptyOrNil(formik.values.mailingCountryCode)) {
      effects.apis
        .getStatesByCountryId({ countryId: formik.values.mailingCountryCode })
        .then((states) => {
          if (isEmpty(states)) {
            setRegionMailingOptions([])
          } else {
            setRegionMailingOptions(states.data)
          }
        })
    } else {
      setRegionMailingOptions([])
    }
  }, [formik.values.mailingCountryCode])

  return (
    <div className="w-full">
      <div>
        <div className="text-blue-950 mb-5">
          <FormattedMessage id="crm.profile.contentName.physicalAddress" />
        </div>
        <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
          <LabelInput
            label={formatMessage({
              id: 'subscriber.label.country',
            })}
          >
            <CountrySelector
              id={'physicalCountryCode'}
              placeholder={formatMessage({
                id: 'subscriber.label.country',
              })}
              className={twMerge('w-full', classes.inputFormat)}
              countries={countries}
              value={formik.values.physicalCountryCode || null}
              onChange={(value) => {
                formik.setFieldValue('physicalStateId', null)
                formik.setFieldValue(
                  'physicalCountryCode',
                  isEmpty(value) ? '' : value.id,
                )
                formik.setFieldTouched('physicalCountryCode', true, false)
                if (
                  isEmpty(value) ||
                  isNil(value) ||
                  propEq('regionType', 'none', value)
                ) {
                  setRegionTypeOfPhysical(null)
                } else {
                  setRegionTypeOfPhysical(value.regionType)
                }
              }}
            />
          </LabelInput>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.physicalStreetAddress',
            })}
          >
            <SimpleInput
              id="physicalStreetAddress1"
              name="physicalStreetAddress1"
              type="text"
              value={formik.values.physicalStreetAddress1}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue(
                  'physicalStreetAddress1',
                  event.target.value,
                )
                formik.setFieldTouched('physicalStreetAddress1', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.physicalStreetAddress',
              })}
              error={
                formik.errors.physicalStreetAddress1 &&
                formik.touched.physicalStreetAddress1
              }
              helperText={formik.errors.physicalStreetAddress1}
            />
          </LabelInput>
        </div>
        <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.physicalAddressLine2',
            })}
          >
            <SimpleInput
              id="physicalStreetAddress2"
              name="physicalStreetAddress2"
              type="text"
              value={formik.values.physicalStreetAddress2}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue(
                  'physicalStreetAddress2',
                  event.target.value,
                )
                formik.setFieldTouched('physicalStreetAddress2', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.physicalAddressLine2',
              })}
              error={
                formik.errors.physicalStreetAddress2 &&
                formik.touched.physicalStreetAddress2
              }
              helperText={formik.errors.physicalStreetAddress2}
            />
          </LabelInput>
          <LabelInput
            label={formatMessage({
              id: 'subscriber.label.stateProvinceRegion',
            })}
          >
            <SimpleInput
              id="physicalProvinceName"
              name="physicalProvinceName"
              type="text"
              value={formik.values.physicalProvinceName}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue('physicalProvinceName', event.target.value)
                formik.setFieldTouched('physicalProvinceName', true, false)
              }}
              placeholder={formatMessage({
                id: 'subscriber.label.stateProvinceRegion',
              })}
              error={
                formik.errors.physicalProvinceName &&
                formik.touched.physicalProvinceName
              }
              helperText={formik.errors.physicalProvinceName}
            />
          </LabelInput>
        </div>
        <div
          className={clsx(
            isDownXs ? 'space-x-0 w-full' : 'flex space-x-10',
            'w-full',
          )}
        >
          <LabelInput
            label={formatMessage({
              id: 'subscriber.label.city',
            })}
          >
            <SimpleInput
              id="physicalCityName"
              name="physicalCityName"
              type="text"
              value={formik.values.physicalCityName}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue('physicalCityName', event.target.value)
                formik.setFieldTouched('physicalCityName', true, false)
              }}
              placeholder={formatMessage({
                id: 'subscriber.label.city',
              })}
              error={
                formik.errors.physicalCityName &&
                formik.touched.physicalCityName
              }
              helperText={formik.errors.physicalCityName}
            />
          </LabelInput>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.physicalPostalCode',
            })}
          >
            <SimpleInput
              id="physicalPostalCode"
              name="physicalPostalCode"
              type="text"
              value={formik.values.physicalPostalCode}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue('physicalPostalCode', event.target.value)
                formik.setFieldTouched('physicalPostalCode', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.physicalPostalCode',
              })}
              error={
                formik.errors.physicalPostalCode &&
                formik.touched.physicalPostalCode
              }
              helperText={formik.errors.physicalPostalCode}
            />
          </LabelInput>
        </div>
      </div>
      <div>
        <div className="text-blue-950 mb-5 mt-10">
          <FormattedMessage id="crm.form.mailingStreetAddress" />
        </div>
        <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
          <LabelInput
            label={formatMessage({
              id: 'subscriber.label.country',
            })}
          >
            <CountrySelector
              id={'mailingCountryCode'}
              placeholder={formatMessage({
                id: 'subscriber.label.country',
              })}
              countries={countries}
              className={twMerge('w-full', classes.inputFormat)}
              value={formik.values.mailingCountryCode || null}
              onChange={(value) => {
                formik.setFieldValue('mailingStateId', null)
                formik.setFieldValue(
                  'mailingCountryCode',
                  isEmpty(value) ? '' : value.id,
                )
                formik.setFieldTouched('mailingCountryCode', true, false)
                if (
                  isEmpty(value) ||
                  isNil(value) ||
                  propEq('regionType', 'none', value)
                ) {
                  setRegionTypeOfMilling(null)
                } else {
                  setRegionTypeOfMilling(value.regionType)
                }
              }}
            />
          </LabelInput>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.mailingStreetAddress',
            })}
          >
            <SimpleInput
              id="mailingStreetAddress1"
              name="mailingStreetAddress1"
              type="text"
              value={formik.values.mailingStreetAddress1}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue(
                  'mailingStreetAddress1',
                  event.target.value,
                )
                formik.setFieldTouched('mailingStreetAddress1', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.mailingStreetAddress',
              })}
              error={
                formik.errors.mailingStreetAddress1 &&
                formik.touched.mailingStreetAddress1
              }
              helperText={formik.errors.mailingStreetAddress1}
            />
          </LabelInput>
        </div>
        <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.mailingStreetAddressLine2',
            })}
          >
            <SimpleInput
              id="mailingStreetAddress2"
              name="mailingStreetAddress2"
              type="text"
              value={formik.values.mailingStreetAddress2}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue(
                  'mailingStreetAddress2',
                  event.target.value,
                )
                formik.setFieldTouched('mailingStreetAddress2', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.mailingStreetAddressLine2',
              })}
              error={
                formik.errors.mailingStreetAddress2 &&
                formik.touched.mailingStreetAddress2
              }
              helperText={formik.errors.mailingStreetAddress2}
            />
          </LabelInput>
          <LabelInput
            label={formatMessage({
              id: 'crm.form.mailingCityName',
            })}
          >
            <SimpleInput
              id="mailingCityName"
              name="mailingCityName"
              type="text"
              value={formik.values.mailingCityName}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue('mailingCityName', event.target.value)
                formik.setFieldTouched('mailingCityName', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.mailingCityName',
              })}
              error={
                formik.errors.mailingCityName && formik.touched.mailingCityName
              }
              helperText={formik.errors.mailingCityName}
            />
          </LabelInput>
        </div>

        <div
          className={clsx(
            isDownXs ? 'space-x-0 w-full' : 'flex space-x-10',
            isEmptyOrNil(regionMailingOptions) ? 'sm:w-1/2 pr-5' : 'w-full',
          )}
        >
          {!isEmptyOrNil(regionMailingOptions) && (
            <LabelInput
              label={getRegionTypes(regionTypeOfMilling, formatMessage)}
            >
              <RegionSelector
                id="mailingStateId"
                name="mailingStateId"
                className={twMerge('w-full', classes.inputFormat)}
                value={formik.values.mailingStateId || null}
                placeholder={getRegionTypes(regionTypeOfMilling, formatMessage)}
                regions={regionMailingOptions}
                onChange={(value) => {
                  formik.setFieldValue('mailingStateId', value)
                  formik.setFieldTouched('mailingStateId', true, false)
                }}
              />
            </LabelInput>
          )}
          <LabelInput
            label={formatMessage({
              id: 'crm.form.mailingPostalCode',
            })}
          >
            <SimpleInput
              id="mailingPostalCode"
              name="mailingPostalCode"
              type="text"
              value={formik.values.mailingPostalCode}
              className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
              onChange={(event) => {
                formik.setFieldValue('mailingPostalCode', event.target.value)
                formik.setFieldTouched('mailingPostalCode', true, false)
              }}
              placeholder={formatMessage({
                id: 'crm.form.mailingPostalCode',
              })}
              error={
                formik.errors.mailingPostalCode &&
                formik.touched.mailingPostalCode
              }
              helperText={formik.errors.mailingPostalCode}
            />
          </LabelInput>
        </div>
      </div>
      <div className="flex justify-end space-x-5 mt-5">
        <ContainedButton
          color={CLOSE}
          onClick={onCancelClick}
          disabled={isButtonDisabled || isButtonLoading}
          type="button"
          className="w-96"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'action.cancel',
              })}
            </span>
          </div>
        </ContainedButton>
        <ContainedButton
          color={SECONDARY}
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          type="submit"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'subscriberCrm.editGroup.save.label',
              })}
            </span>
          </div>
        </ContainedButton>
      </div>
    </div>
  )
}

export default AddressInformationForm
